import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { usePaginatedQuery } from './UsePaginatedQuery'
import { useOptimisticDelete, useOptimisticUpdate } from './UseOptimisticMutation'
import type {
	Concept,
	ConceptAssignmentInput,
	Formation,
	PresetRoute,
	RoleTerminology,
} from '../types/Concept.types'
import {
	conceptKeys,
	createConcept as apiCreateConcept,
	createFormation as apiCreateFormation,
	deleteConcept as apiDeleteConcept,
	deleteFormation as apiDeleteFormation,
	fetchFormationsPage,
	fetchPresetRoutes,
	fetchRoles,
	updateConcept as apiUpdateConcept,
	updateFormation as apiUpdateFormation,
	updateRoles as apiUpdateRoles,
} from '../api/queries/ConceptQueries'
import { playKeys } from '../api/queries/PlayQueries'

interface UseConceptDataReturn {
	formations: Formation[]
	presetRoutes: PresetRoute[]
	roles: RoleTerminology[]
	isLoading: boolean
	error: string | null
	createFormation: (data: {
		name: string
		description?: string
		hashMode?: boolean
		thumbnail?: string | null
		positions: Array<{
			role: string
			x: number
			y: number
			color?: string
			isLineman?: boolean
			hashRelative?: boolean
			hashMark?: string | null
		}>
	}) => Promise<Formation>
	updateFormation: (id: string, data: Partial<Formation>) => Promise<void>
	deleteFormation: (id: string) => Promise<void>
	createConcept: (data: {
		name: string
		description?: string
		targeting_mode: 'absolute_role' | 'relative_selector'
		ball_position?: 'left' | 'center' | 'right'
		play_direction?: 'left' | 'right' | 'na'
		playbook_id?: string
		assignments: ConceptAssignmentInput[]
	}) => Promise<Concept>
	updateConcept: (id: string, data: Partial<Concept>) => Promise<void>
	deleteConcept: (id: string) => Promise<void>
	updateRoleTerminology: (roles: RoleTerminology[]) => Promise<void>
	refetch: () => Promise<void>
	fetchNextFormations: () => Promise<void>
	hasMoreFormations: boolean
}

/**
 * Custom hook for managing concept architecture data with React Query
 *
 * Fetches and manages all concept-related data for a playbook using React Query
 * for automatic caching, background refetching, and optimistic updates.
 *
 * @param playbookId - The playbook ID to fetch data for (required)
 * @returns Object containing data arrays, loading state, error state, and CRUD functions
 *
 * @example
 * ```typescript
 * const {
 *   formations,
 *   concepts,
 *   isLoading,
 *   createConcept,
 *   updateConcept
 * } = useConceptData(playbookId)
 * ```
 *
 * Features:
 * - Automatic caching with React Query (5min stale, 10min cache)
 * - Background refetching on window focus
 * - Optimistic updates for instant UI feedback
 * - Automatic cache invalidation after mutations
 * - Parallel data fetching for optimal performance
 * - Auth handling (redirects to /login on 401)
 */
export function useConceptData(
	playbookId: string | undefined,
): UseConceptDataReturn {
	const navigate = useNavigate()
	const queryClient = useQueryClient()

	// Handle unauthorized errors globally
	const handleUnauthorized = (error: Error) => {
		if (error.message === 'UNAUTHORIZED') {
			navigate('/login')
		}
	}

	// =========================================================================
	// QUERIES
	// =========================================================================

	// Use paginated query for formations (block-based API)
	const {
		items: formations,
		isLoading: formationsLoading,
		error: formationsError,
		fetchNextPage: fetchNextFormations,
		hasNextPage: hasMoreFormations,
		isFetchingNextPage: isFetchingFormations,
		updateItem: updateFormationItem,
		prependItem: prependFormation,
	} = usePaginatedQuery({
		queryKey: conceptKeys.formations(playbookId || ''),
		fetchPage: async (cursor) => {
			if (!playbookId) throw new Error('No playbook ID')
			const result = await fetchFormationsPage(playbookId, 20, cursor)
			return {
				items: result.items,
				nextCursor: result.nextCursor ?? undefined,
				hasMore: !!result.nextCursor,
			}
		},
		getItemId: (formation) => String(formation.id),
		enabled: !!playbookId,
	})

	const {
		data: presetRoutes = [],
		isLoading: routesLoading,
		error: routesError,
	} = useQuery<PresetRoute[]>({
		queryKey: conceptKeys.presetRoutes(),
		queryFn: fetchPresetRoutes,
	})

	const {
		data: roles = [],
		isLoading: rolesLoading,
		error: rolesError,
	} = useQuery<RoleTerminology[]>({
		queryKey: conceptKeys.roles(playbookId || ''),
		queryFn: () => fetchRoles(playbookId!),
		enabled: !!playbookId,
	})

	// =========================================================================
	// MUTATIONS - FORMATIONS
	// =========================================================================

	const createFormationMutation = useMutation({
		mutationFn: (data: Parameters<typeof apiCreateFormation>[1]) =>
			apiCreateFormation(playbookId!, data),
		onSuccess: (newFormation) => {
			// Prepend new formation to local state for instant UI update
			prependFormation(newFormation)
		},
		onError: handleUnauthorized,
	})

	const updateFormationMutation = useOptimisticUpdate<Formation, string, Partial<Formation>>({
		mutationFn: async ({ id, data }) => {
			await apiUpdateFormation(id, data)
		},
		queryKey: conceptKeys.formations(playbookId!),
		getId: (formation) => formation.id,
		applyUpdate: (formation, data) => ({ ...formation, ...data }),
		onError: handleUnauthorized,
	})

	// Wrapper that updates local state and invalidates play list cache
	const updateFormation = async (id: string, data: Partial<Formation>): Promise<void> => {
		await updateFormationMutation(id, data)
		// Update local state immediately for instant UI feedback
		updateFormationItem(id, (formation) => ({ ...formation, ...data }))
		// Invalidate play list cache so thumbnails update in background
		// (uses invalidateQueries instead of refetchQueries to avoid blocking UI)
		queryClient.invalidateQueries({ queryKey: playKeys.lists() })
	}

	const deleteFormation = useOptimisticDelete<Formation, string>({
		mutationFn: async (id) => {
			await apiDeleteFormation(id)
		},
		queryKey: conceptKeys.formations(playbookId!),
		getId: (formation) => formation.id,
		onError: handleUnauthorized,
	})

	// =========================================================================
	// MUTATIONS - CONCEPTS
	// =========================================================================

	const createConceptMutation = useMutation({
		mutationFn: (data: Parameters<typeof apiCreateConcept>[1]) =>
			apiCreateConcept(playbookId!, data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: conceptKeys.baseConcepts(playbookId!),
			})
		},
		onError: handleUnauthorized,
	})

	const updateConcept = useOptimisticUpdate<Concept, string, Partial<Concept>>({
		mutationFn: async ({ id, data }) => {
			await apiUpdateConcept(id, data)
		},
		queryKey: conceptKeys.baseConcepts(playbookId!),
		getId: (concept) => concept.id,
		applyUpdate: (concept, data) => ({ ...concept, ...data }),
		onError: handleUnauthorized,
	})

	const deleteConcept = useOptimisticDelete<Concept, string>({
		mutationFn: async (id) => {
			await apiDeleteConcept(id)
		},
		queryKey: conceptKeys.baseConcepts(playbookId!),
		getId: (concept) => concept.id,
		onError: handleUnauthorized,
	})

	// =========================================================================
	// MUTATIONS - ROLES
	// =========================================================================

	const updateRolesMutation = useMutation({
		mutationFn: (_roles: RoleTerminology[]) => apiUpdateRoles(playbookId!, _roles),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: conceptKeys.roles(playbookId!),
			})
		},
		onError: handleUnauthorized,
	})

	// =========================================================================
	// AGGREGATE STATE
	// =========================================================================

	const isLoading =
		formationsLoading ||
		routesLoading ||
		rolesLoading

	const error =
		formationsError?.message ||
		routesError?.message ||
		rolesError?.message ||
		null

	// =========================================================================
	// WRAPPER FUNCTIONS (maintain API compatibility)
	// =========================================================================

	const createFormation = async (
		data: Parameters<typeof apiCreateFormation>[1],
	): Promise<Formation> => {
		return createFormationMutation.mutateAsync(data)
	}

	const createConcept = async (
		data: Parameters<typeof apiCreateConcept>[1],
	): Promise<Concept> => {
		return createConceptMutation.mutateAsync(data)
	}

	const updateRoleTerminology = async (
		roles: RoleTerminology[],
	): Promise<void> => {
		await updateRolesMutation.mutateAsync(roles)
	}

	const refetch = async (): Promise<void> => {
		await Promise.all([
			queryClient.invalidateQueries({
				queryKey: conceptKeys.formations(playbookId!),
			}),
			queryClient.invalidateQueries({
				queryKey: conceptKeys.baseConcepts(playbookId!),
			}),
			queryClient.invalidateQueries({ queryKey: conceptKeys.presetRoutes() }),
			queryClient.invalidateQueries({ queryKey: conceptKeys.roles(playbookId!) }),
		])
	}

	return {
		formations,
		presetRoutes,
		roles,
		isLoading,
		error,
		createFormation,
		updateFormation,
		deleteFormation,
		createConcept,
		updateConcept,
		deleteConcept,
		updateRoleTerminology,
		refetch,
		fetchNextFormations,
		hasMoreFormations,
	}
}
